import { useState, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Flag, Shield, Clock, User, AlertTriangle, CheckCircle, XCircle, MessageSquare, Gavel, Archive } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ReportDetailDialog } from "./ReportDetailDialog";

interface UserReport {
  id: string;
  report_type: string;
  reason: string;
  description?: string;
  evidence_urls?: string[];
  status: string;
  admin_notes?: string;
  reviewed_at?: string;
  created_at: string;
  reporter: {
    full_name: string;
    email: string;
    user_type: string;
    platform_source?: string;
  };
  reported_user: {
    id: string;
    full_name: string;
    email: string;
    user_type: string;
    platform_source?: string;
    is_suspended: boolean;
    suspension_reason?: string;
    suspended_until?: string;
  };
  job?: {
    title: string;
    id: string;
  };
  reviewed_by?: {
    full_name: string;
  };
}

interface UserSanction {
  id: string;
  sanction_type: string;
  reason: string;
  description?: string;
  duration_days?: number;
  imposed_at: string;
  expires_at?: string;
  is_active: boolean;
  imposed_by: {
    full_name: string;
  };
}

export const AdminReportsManagement = () => {
  const [reports, setReports] = useState<UserReport[]>([]);
  const [archivedReports, setArchivedReports] = useState<UserReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<UserReport | null>(null);
  const [showReportDetail, setShowReportDetail] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"active" | "archive">("active");
  const [adminNotes, setAdminNotes] = useState("");
  const [sanctionType, setSanctionType] = useState("");
  const [sanctionReason, setSanctionReason] = useState("");
  const [sanctionDuration, setSanctionDuration] = useState<number | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [userSanctions, setUserSanctions] = useState<UserSanction[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    console.log("AdminReportsManagement: Component mounted, fetching reports");
    fetchReports();
    const cleanup = setupRealtimeSubscription();

    return cleanup;
  }, [statusFilter]);

  const fetchReports = async () => {
    try {
      console.log("AdminReportsManagement: Fetching reports...");
      
      // Check current user and admin status
      const { data: { user } } = await supabase.auth.getUser();
      console.log("Current user:", user?.id);
      
      const { data: adminCheck } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user?.id)
        .eq('role', 'admin')
        .limit(1);
      
      console.log("Admin role check:", adminCheck?.length > 0 ? 'Has admin role' : 'No admin role');

      // Always fetch all reports, we'll filter them for display
      const activeStatuses = ["pending", "reviewed"];
      const archivedStatuses = ["resolved", "dismissed"];
      
      // Fetch user reports (base rows; enrich manually)
      let userBaseQuery = supabase
        .from("user_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        userBaseQuery = userBaseQuery.eq("status", statusFilter);
      }

      // Fetch chat reports (base rows only; enrich manually to avoid FK dependency)
      let chatBaseQuery = supabase
        .from("chat_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        chatBaseQuery = chatBaseQuery.eq("status", statusFilter);
      }

      // Fetch individual message reports as well (from admin_message_reports)
      let msgBaseQuery = supabase
        .from("admin_message_reports")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        msgBaseQuery = msgBaseQuery.eq("status", statusFilter);
      }

      const [
        { data: userData, error: userError },
        { data: chatRows, error: chatError },
        { data: msgRows, error: msgError }
      ] = await Promise.all([
        userBaseQuery,
        chatBaseQuery,
        msgBaseQuery,
      ]);

      if (userError) throw userError;
      if (chatError) throw chatError;
      if (msgError) throw msgError;

      console.log("AdminReportsManagement: Raw data fetched", {
        userReports: userData?.length || 0,
        chatReports: chatRows?.length || 0,
        messageReports: msgRows?.length || 0
      });

      // Map user-level reports by enriching base rows
      const userRows = userData || [];
      let mappedUserReports: any[] = [];
      if (userRows.length > 0) {
        const reporterIds = Array.from(new Set(userRows.map((r: any) => r.reporter_id).filter(Boolean)));
        const reportedIds = Array.from(new Set(userRows.map((r: any) => r.reported_user_id).filter(Boolean)));
        const reviewerIds = Array.from(new Set(userRows.map((r: any) => r.reviewed_by).filter(Boolean)));
        const jobIds = Array.from(new Set(userRows.map((r: any) => r.job_id).filter(Boolean)));

        const [reportersRes, reportedRes, reviewersRes, jobsRes] = await Promise.all([
          reporterIds.length
            ? supabase.from('profiles').select('id, full_name, email, user_type, platform_source').in('id', reporterIds as string[])
            : Promise.resolve({ data: [] as any[] }),
          reportedIds.length
            ? supabase.from('profiles').select('id, full_name, email, user_type, platform_source, is_suspended, suspension_reason, suspended_until').in('id', reportedIds as string[])
            : Promise.resolve({ data: [] as any[] }),
          reviewerIds.length
            ? supabase.from('profiles').select('id, full_name').in('id', reviewerIds as string[])
            : Promise.resolve({ data: [] as any[] }),
          jobIds.length
            ? supabase.from('jobs').select('id, title').in('id', jobIds as string[])
            : Promise.resolve({ data: [] as any[] }),
        ] as const);

        const reporters = new Map((reportersRes.data || []).map((p: any) => [p.id, p]));
        const reported = new Map((reportedRes.data || []).map((p: any) => [p.id, p]));
        const reviewers = new Map((reviewersRes.data || []).map((p: any) => [p.id, p]));
        const jobs = new Map((jobsRes.data || []).map((j: any) => [j.id, j]));

        mappedUserReports = userRows.map((r: any) => ({
          id: r.id,
          report_type: r.report_type || 'user',
          reason: r.reason,
          description: r.description,
          evidence_urls: r.evidence_urls,
          status: r.status,
          admin_notes: r.admin_notes,
          reviewed_at: r.reviewed_at,
          created_at: r.created_at,
          reporter: reporters.get(r.reporter_id) || { full_name: 'Unknown', email: '', user_type: '', platform_source: 'sortable' },
          reported_user: reported.get(r.reported_user_id) || { id: r.reported_user_id, full_name: 'Unknown', email: '', user_type: '', platform_source: 'sortable', is_suspended: false },
          job: r.job_id ? (jobs.get(r.job_id) || { id: r.job_id, title: 'Unknown job' }) : undefined,
          reviewed_by: r.reviewed_by ? { full_name: (reviewers.get(r.reviewed_by)?.full_name || 'Unknown') } : null,
        }));
      }

      // If there are chat reports, load related profiles and jobs in bulk
      const chatData = chatRows || [];
      let mappedChatReports: any[] = [];

      if (chatData.length > 0) {
        const reporterIds = Array.from(new Set(chatData.map((r: any) => r.reporter_id).filter(Boolean)));
        const reportedIds = Array.from(new Set(chatData.map((r: any) => r.reported_user_id).filter(Boolean)));
        const reviewerIds = Array.from(new Set(chatData.map((r: any) => r.reviewed_by).filter(Boolean)));
        const jobIds = Array.from(new Set(chatData.map((r: any) => r.job_id).filter(Boolean)));

        const [reportersRes, reportedRes, reviewersRes, jobsRes] = await Promise.all([
          reporterIds.length
            ? supabase.from('profiles').select('id, full_name, email, user_type, platform_source').in('id', reporterIds as string[])
            : Promise.resolve({ data: [] as any[] }),
          reportedIds.length
            ? supabase.from('profiles').select('id, full_name, email, user_type, platform_source, is_suspended, suspension_reason, suspended_until').in('id', reportedIds as string[])
            : Promise.resolve({ data: [] as any[] }),
          reviewerIds.length
            ? supabase.from('profiles').select('id, full_name').in('id', reviewerIds as string[])
            : Promise.resolve({ data: [] as any[] }),
          jobIds.length
            ? supabase.from('jobs').select('id, title').in('id', jobIds as string[])
            : Promise.resolve({ data: [] as any[] }),
        ] as const);

        const reporters = new Map((reportersRes.data || []).map((p: any) => [p.id, p]));
        const reported = new Map((reportedRes.data || []).map((p: any) => [p.id, p]));
        const reviewers = new Map((reviewersRes.data || []).map((p: any) => [p.id, p]));
        const jobs = new Map((jobsRes.data || []).map((j: any) => [j.id, j]));

        mappedChatReports = chatData.map((r: any) => ({
          id: r.id,
          report_type: 'chat',
          reason: r.report_reason,
          description: r.report_description,
          evidence_urls: undefined,
          status: r.status,
          admin_notes: r.admin_notes,
          reviewed_at: r.reviewed_at,
          created_at: r.created_at,
          reporter: reporters.get(r.reporter_id) || { full_name: 'Unknown', email: '', user_type: '', platform_source: 'sortable' },
          reported_user: reported.get(r.reported_user_id) || { id: r.reported_user_id, full_name: 'Unknown', email: '', user_type: '', platform_source: 'sortable', is_suspended: false },
          job: jobs.get(r.job_id) || { id: r.job_id, title: 'Unknown job' },
          reviewed_by: r.reviewed_by ? { full_name: (reviewers.get(r.reviewed_by)?.full_name || 'Unknown') } : null,
        }));
      }

      // Map message-level reports (we may not be able to fetch message sender due to RLS on messages)
      const msgData = msgRows || [];
      let mappedMsgReports: any[] = [];
      if (msgData.length > 0) {
        const reporterIds = Array.from(new Set(msgData.map((r: any) => r.reporter_id).filter(Boolean)));
        const jobIds = Array.from(new Set(msgData.map((r: any) => r.job_id).filter(Boolean)));

        const [reportersRes, jobsRes] = await Promise.all([
          reporterIds.length
            ? supabase.from('profiles').select('id, full_name, email, user_type, platform_source').in('id', reporterIds as string[])
            : Promise.resolve({ data: [] as any[] }),
          jobIds.length
            ? supabase.from('jobs').select('id, title').in('id', jobIds as string[])
            : Promise.resolve({ data: [] as any[] }),
        ] as const);

        const reporters = new Map((reportersRes.data || []).map((p: any) => [p.id, p]));
        const jobs = new Map((jobsRes.data || []).map((j: any) => [j.id, j]));

        mappedMsgReports = msgData.map((r: any) => ({
          id: r.id,
          report_type: 'message',
          reason: r.report_reason,
          description: r.report_description,
          evidence_urls: undefined,
          status: r.status,
          admin_notes: r.admin_notes,
          reviewed_at: r.reviewed_at,
          created_at: r.created_at,
          reporter: reporters.get(r.reporter_id) || { full_name: 'Unknown', email: '', user_type: '', platform_source: 'sortable' },
          reported_user: { id: '', full_name: 'Unknown (message sender)', email: '', user_type: '', platform_source: 'sortable', is_suspended: false },
          job: jobs.get(r.job_id) || { id: r.job_id, title: 'Unknown job' },
          reviewed_by: r.reviewed_by ? { full_name: 'Unknown' } : null,
        }));
      }

      const combined = [...mappedUserReports, ...mappedChatReports, ...mappedMsgReports].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      console.log("AdminReportsManagement: Final combined reports", {
        totalReports: combined.length,
        userReports: mappedUserReports.length,
        chatReports: mappedChatReports.length,
        messageReports: mappedMsgReports.length,
        sampleChatReports: mappedChatReports.slice(0, 2)
      });

      // Separate active and archived reports
      const activeReports = combined.filter(report => activeStatuses.includes(report.status));
      const archivedReports = combined.filter(report => archivedStatuses.includes(report.status));
      
      // Set both states so they're available immediately
      setReports(activeReports);
      setArchivedReports(archivedReports);
    } catch (error: any) {
      toast({
        title: "Error loading reports",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserSanctions = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_sanctions")
        .select(`
          *,
          imposed_by:profiles!user_sanctions_imposed_by_fkey(full_name)
        `)
        .eq("user_id", userId)
        .order("imposed_at", { ascending: false });

      if (error) throw error;
      setUserSanctions(data || []);
    } catch (error: any) {
      console.error("Error fetching user sanctions:", error);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel(`admin-reports-channel-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_reports'
        },
        () => {
          fetchReports();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_reports'
        },
        () => {
          fetchReports();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_message_reports'
        },
        () => {
          fetchReports();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const updateReportStatus = async (reportId: string, status: string, notes?: string) => {
    setIsProcessing(true);
    try {
      const report = reports.find(r => r.id === reportId);
      if (!report) throw new Error("Report not found");

      // Get current admin user
      const { data: user, error: userError } = await supabase.auth.getUser();
      console.log('Current user:', user.user?.id, 'Error:', userError);
      
      if (!user.user) throw new Error("Not authenticated");

      // Try to get admin profile with error handling
      let { data: adminProfile, error: profileError } = await supabase
        .from("profiles")
        .select("id, user_type")
        .eq("user_id", user.user.id)
        .single();

      console.log('Admin profile query result:', { adminProfile, profileError });

      // If profile doesn't exist, create it
      if (!adminProfile) {
        console.error('Admin profile not found for user:', user.user.id);
        const { data: newProfile, error: insertError } = await supabase
          .from("profiles")
          .insert({
            user_id: user.user.id,
            email: user.user.email || '',
            full_name: user.user.email || 'Admin User',
            user_type: 'customer',
            phone: 'PHONE_REQUIRED'
          })
          .select("id, user_type")
          .single();
        
        if (insertError) {
          console.error('Error creating admin profile:', insertError);
          throw new Error(`Admin profile not found and could not be created: ${insertError.message}`);
        }
        
        console.log('Created new admin profile:', newProfile);
        adminProfile = newProfile; // Use the newly created profile
      }

      const updateData = {
        status,
        admin_notes: notes || null,
        reviewed_at: new Date().toISOString(),
        reviewed_by: adminProfile.id
      };

      let error;
      // Update the appropriate table based on report type
      if (report.report_type === 'chat') {
        const { error: chatError } = await supabase
          .from("chat_reports")
          .update(updateData)
          .eq("id", reportId);
        error = chatError;
      } else if (report.report_type === 'message') {
        const { error: msgError } = await supabase
          .from("admin_message_reports")
          .update(updateData)
          .eq("id", reportId);
        error = msgError;
      } else {
        const { error: userError } = await supabase
          .from("user_reports")
          .update(updateData)
          .eq("id", reportId);
        error = userError;
      }

      if (error) {
        console.error('Error updating report:', error);
        throw error;
      }

      toast({
        title: "Report updated",
        description: `Report status changed to ${status}`,
      });

      fetchReports();
    } catch (error: any) {
      console.error('Full error in updateReportStatus:', error);
      toast({
        title: "Error updating report",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const imposeSanction = async () => {
    if (!selectedReport || !sanctionType || !sanctionReason.trim()) return;

    setIsProcessing(true);
    try {
      const expiresAt = sanctionDuration 
        ? new Date(Date.now() + sanctionDuration * 24 * 60 * 60 * 1000).toISOString()
        : null;

      // Get current admin user
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      let { data: adminProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.user.id)
        .single();

      if (!adminProfile) {
        const { data: newProfile } = await supabase
          .from("profiles")
          .insert({
            user_id: user.user.id,
            email: user.user.email || '',
            full_name: user.user.email || 'Admin User',
            user_type: 'customer',
            phone: 'PHONE_REQUIRED'
          })
          .select("id")
          .single();
        adminProfile = newProfile;
      }

      const { error } = await supabase
        .from("user_sanctions")
        .insert({
          user_id: selectedReport.reported_user.id,
          sanction_type: sanctionType,
          reason: sanctionReason,
          duration_days: sanctionDuration,
          expires_at: expiresAt,
          imposed_by: adminProfile?.id
        });

      if (error) throw error;

      // Update user profile if suspension
      if (sanctionType === "suspension") {
        const { error: profileError } = await supabase
          .from("profiles")
          .update({
            is_suspended: true,
            suspension_reason: sanctionReason,
            suspended_until: expiresAt
          })
          .eq("id", selectedReport.reported_user.id);

        if (profileError) throw profileError;
      }

      toast({
        title: "Sanction imposed",
        description: `${sanctionType} imposed on user`,
      });

      // Reset form
      setSanctionType("");
      setSanctionReason("");
      setSanctionDuration(undefined);
      
      fetchReports();
      if (selectedReport) {
        fetchUserSanctions(selectedReport.reported_user.id);
      }
    } catch (error: any) {
      toast({
        title: "Error imposing sanction",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getSeverityIcon = (reportType: string) => {
    switch (reportType) {
      case 'chat':
        return <MessageSquare className="h-5 w-5 text-orange-500" />;
      case 'message':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'account':
        return <User className="h-5 w-5 text-purple-500" />;
      default:
        return <Flag className="h-5 w-5 text-blue-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'border-yellow-500 text-yellow-700 bg-yellow-50';
      case 'reviewed':
        return 'border-blue-500 text-blue-700 bg-blue-50';
      case 'resolved':
        return 'border-green-500 text-green-700 bg-green-50';
      case 'dismissed':
        return 'border-gray-500 text-gray-700 bg-gray-50';
      default:
        return 'border-gray-300 text-gray-600 bg-gray-50';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Reports Management</h1>
            <p className="text-muted-foreground">Review and manage user reports</p>
          </div>
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Reports</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="reviewed">Under Review</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as "active" | "archive")} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Flag className="h-4 w-4" />
            Active Reports ({reports.length})
          </TabsTrigger>
          <TabsTrigger value="archive" className="flex items-center gap-2">
            <Archive className="h-4 w-4" />
            Archive ({archivedReports.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="space-y-6">
          {/* Active Reports Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Pending</p>
                    <p className="text-2xl font-bold">
                      {reports.filter(r => r.status === 'pending').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Flag className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Under Review</p>
                    <p className="text-2xl font-bold">
                      {reports.filter(r => r.status === 'reviewed').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Resolved</p>
                    <p className="text-2xl font-bold">
                      {[...reports, ...archivedReports].filter(r => r.status === 'resolved').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Dismissed</p>
                    <p className="text-2xl font-bold">
                      {[...reports, ...archivedReports].filter(r => r.status === 'dismissed').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Active Reports List */}
          <Card>
            <CardHeader>
              <CardTitle>Active Reports</CardTitle>
              <CardDescription>
                Reports that require attention - click to review and take action
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reports.length === 0 ? (
                <div className="text-center py-12">
                  <Flag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No active reports</h3>
                  <p className="text-muted-foreground">
                    All reports have been resolved or dismissed
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[600px]">
                  <div className="space-y-4">
                    {reports.map((report) => (
                      <Card 
                        key={report.id} 
                        className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-orange-500"
                        onClick={() => {
                          setSelectedReport(report);
                          setShowReportDetail(true);
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {getSeverityIcon(report.report_type)}
                              <div>
                                <h4 className="font-semibold capitalize">
                                  {report.report_type.replace('_', ' ')} Report
                                </h4>
                                <p className="text-sm text-muted-foreground">
                                  {format(new Date(report.created_at), "PPP 'at' p")}
                                </p>
                              </div>
                            </div>
                            <Badge 
                              variant="outline"
                              className={getStatusColor(report.status)}
                            >
                              {report.status}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p><strong>Reporter:</strong> {report.reporter.full_name}</p>
                              <p><strong>Type:</strong> {report.reporter.platform_source === 'movable' ? (report.reporter.user_type === 'tradesman' ? 'mover' : 'moving customer') : report.reporter.user_type}</p>
                            </div>
                            <div>
                              <p><strong>Reported:</strong> {report.reported_user.full_name}</p>
                              <p><strong>Type:</strong> {report.reported_user.platform_source === 'movable' ? (report.reported_user.user_type === 'tradesman' ? 'mover' : 'moving customer') : report.reported_user.user_type}</p>
                              {report.reported_user.is_suspended && (
                                <Badge variant="destructive" className="text-xs mt-1">
                                  SUSPENDED
                                </Badge>
                              )}
                            </div>
                          </div>

                          <p className="text-sm text-muted-foreground mt-2 truncate">
                            <strong>Reason:</strong> {report.reason}
                          </p>
                          
                          {report.job && (
                            <p className="text-sm text-muted-foreground mt-1 truncate">
                              <strong>Job:</strong> {report.job.title}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="archive" className="space-y-6">
          {/* Archive Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Resolved</p>
                    <p className="text-2xl font-bold">
                      {archivedReports.filter(r => r.status === 'resolved').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-gray-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Dismissed</p>
                    <p className="text-2xl font-bold">
                      {archivedReports.filter(r => r.status === 'dismissed').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Archived Reports List */}
          <Card>
            <CardHeader>
              <CardTitle>Archived Reports</CardTitle>
              <CardDescription>
                Reports that have been resolved or dismissed
              </CardDescription>
            </CardHeader>
            <CardContent>
              {archivedReports.length === 0 ? (
                <div className="text-center py-12">
                  <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No archived reports</h3>
                  <p className="text-muted-foreground">
                    Resolved and dismissed reports will appear here
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[600px]">
                  <div className="space-y-4">
                    {archivedReports.map((report) => (
                      <Card 
                        key={report.id} 
                        className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-gray-300"
                        onClick={() => {
                          setSelectedReport(report);
                          setShowReportDetail(true);
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {getSeverityIcon(report.report_type)}
                              <div>
                                <h4 className="font-semibold capitalize">
                                  {report.report_type.replace('_', ' ')} Report
                                </h4>
                                <p className="text-sm text-muted-foreground">
                                  {format(new Date(report.created_at), "PPP 'at' p")}
                                </p>
                              </div>
                            </div>
                            <Badge 
                              variant="outline"
                              className={getStatusColor(report.status)}
                            >
                              {report.status}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p><strong>Reporter:</strong> {report.reporter.full_name}</p>
                              <p><strong>Type:</strong> {report.reporter.platform_source === 'movable' ? (report.reporter.user_type === 'tradesman' ? 'mover' : 'moving customer') : report.reporter.user_type}</p>
                            </div>
                            <div>
                              <p><strong>Reported:</strong> {report.reported_user.full_name}</p>
                              <p><strong>Type:</strong> {report.reported_user.platform_source === 'movable' ? (report.reported_user.user_type === 'tradesman' ? 'mover' : 'moving customer') : report.reported_user.user_type}</p>
                              {report.reported_user.is_suspended && (
                                <Badge variant="destructive" className="text-xs mt-1">
                                  SUSPENDED
                                </Badge>
                              )}
                            </div>
                          </div>

                          <p className="text-sm text-muted-foreground mt-2 truncate">
                            <strong>Reason:</strong> {report.reason}
                          </p>
                          
                          {report.job && (
                            <p className="text-sm text-muted-foreground mt-1 truncate">
                              <strong>Job:</strong> {report.job.title}
                            </p>
                          )}

                          {report.reviewed_at && (
                            <p className="text-xs text-muted-foreground mt-2">
                              <strong>Reviewed:</strong> {format(new Date(report.reviewed_at), "PPP 'at' p")}
                              {report.reviewed_by && (
                                <span> by {report.reviewed_by.full_name}</span>
                              )}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Report Detail Dialog */}
      <ReportDetailDialog
        report={selectedReport}
        isOpen={showReportDetail}
        onClose={() => {
          setShowReportDetail(false);
          setSelectedReport(null);
        }}
        onStatusUpdate={updateReportStatus}
      />
    </div>
  );
};